import React, { FC, useContext, useState } from 'react'
import { Descriptions, Flex, Popover, Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import { ReadOnlyInventorySummaryOrderNode } from '../Show'
import Moment from '@src/global/Moment'
import { OrderStatus } from '@src/graphql/Order/Types'
import { StatusChangeNode } from '@src/generated/schema'
import { useMutation } from '@apollo/client/react'
import { Order } from '@src/graphql/Order'
import { MeContext, Permissions, TOAST_TYPES, ToastContext } from '@src/components/Providers'

export interface DetailProps {
  order: ReadOnlyInventorySummaryOrderNode
}

const findStatusChangeByNewStatus = (
  statusChanges: readonly StatusChangeNode[] | undefined,
  newStatus: OrderStatus,
) => {
  return statusChanges?.find((statusChange) => {
    return statusChange.newStatus.toString() === newStatus.toString()
  })
}

export const Detail: FC<DetailProps> = ({ order }) => {
  const { t } = useTranslation()

  const loadedStatusChange = findStatusChangeByNewStatus(order.statusChanges, OrderStatus.Loaded)
  const finalizedStatusChange = findStatusChangeByNewStatus(order.statusChanges, OrderStatus.Finished)

  const loadedPopoverContent = <Moment format='DD.MM.YYYY HH:mm'>{loadedStatusChange?.dispatchedAt}</Moment>
  const finishedPopoverContent = <Moment format='DD.MM.YYYY HH:mm'>{finalizedStatusChange?.dispatchedAt}</Moment>

  const { setToast } = useContext(ToastContext)
  const [isPlausible, setIsPlausible] = useState(order.isPlausible)
  const [isPlausibleSubmitting, setIsPlausibleSubmitting] = useState(false)
  const [editOrderIsPlausible, { error }] = useMutation(Order.editOrderIsPlausible)
  const { userHasPermission } = useContext(MeContext)
  const canChangeFinishedOrder = userHasPermission(Permissions.CAN_CHANGE_FINISHED_ORDER)

  const onPlausibleChange = async (checked: boolean) => {
    setIsPlausibleSubmitting(true)
    if (!error) {
      const { data }: { data: any } = await editOrderIsPlausible({
        variables: {
          orderId: order.id,
          isPlausible: checked,
        },
      })

      setToast({
        type: TOAST_TYPES.SUCCESS,
        message: t('desktop.pages.orders.alter.form.submit.success.message'),
        description: t('desktop.pages.orders.alter.form.submit.success.description'),
      })

      setIsPlausible(data.editOrderIsPlausible.order.isPlausible)
    } else {
      setToast({
        type: TOAST_TYPES.ERROR,
        message: t('desktop.pages.orders.show.isPlausible.error.message'),
        description: t('desktop.pages.orders.show.isPlausible.error.description'),
      })
    }

    setIsPlausibleSubmitting(false)
  }

  return (
    <>
      {order && (
        <Descriptions>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.number')}>{order.number}</Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.createdBy')}>
            {order.createdBy ? `${order.createdBy.firstName} ${order.createdBy.lastName}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.createdAt')}>
            {order.createdAt && <Moment format='DD.MM.YYYY'>{order.createdAt}</Moment>}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.lastModifiedAt')}>
            {order.lastmodifiedAt && <Moment format='DD.MM.YYYY'>{order.lastmodifiedAt}</Moment>}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.lastModifiedBy')}>
            {order.lastmodifiedBy ? `${order.lastmodifiedBy.firstName} ${order.lastmodifiedBy.lastName}` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.supplier')}>
            {order.supplier && order.supplier.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.customer')}>
            {order.customer.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.status')}>
            {t(`desktop.pages.orders.show.values.status.${order.status}`)}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.driver')}>
            {order.driver && order.driver.firstName} {order.driver && order.driver.lastName}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.vehicle')}>
            {order.vehicle && order.vehicle.registrationNumber}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.loadedMilage')}>
            {loadedStatusChange?.milage && (
              <Popover
                placement='top'
                title={t('desktop.pages.orders.list.columns.loadedMilage')}
                content={loadedPopoverContent}
              >
                <Flex gap='middle'>
                  {loadedStatusChange.milage}
                  <div>
                    (<Moment format='HH:mm'>{loadedStatusChange.dispatchedAt}</Moment>)
                  </div>
                </Flex>
              </Popover>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.finishedMilage')}>
            {finalizedStatusChange?.milage && (
              <Popover
                placement='top'
                title={t('desktop.pages.orders.list.columns.finishedMilage')}
                content={finishedPopoverContent}
              >
                <Flex gap='middle'>
                  {finalizedStatusChange.milage}
                  <div>
                    (<Moment format='HH:mm'>{finalizedStatusChange.dispatchedAt}</Moment>)
                  </div>
                </Flex>
              </Popover>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.list.columns.planDate')}>
            <Moment format='DD.MM.YYYY'>{order.planDate}</Moment>
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.transportMedia')}>
            {order.transportMedia && order.transportMedia.s3Path && (
              <a href={order.transportMedia && order.transportMedia.s3Path} download target='_self'>
                {t('desktop.pages.orders.show.values.transportMedia')}
              </a>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.repeat')}>
            {order.repeat
              ? t('desktop.pages.orders.show.values.repeat.true')
              : t('desktop.pages.orders.show.values.repeat.false')}
          </Descriptions.Item>
          <Descriptions.Item label={t('general.repeatEndDate')}>
            {order.repeatEndDate ? <Moment format='DD.MM.YYYY'>{order.repeatEndDate}</Moment> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.deliveryDate')}>
            {order.finishedAt ? <Moment format='DD.MM.YYYY'>{order.finishedAt}</Moment> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.deliveryNumber')}>
            {order.deliveryNumber && order.deliveryNumber}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.deliveryType')}>
            {t(`desktop.pages.orders.show.values.deliveryType.${order.deliveryType}`)}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.deliveryNoteMedia')}>
            {order.deliveryNoteCustomer?.s3Path && (
              <a href={order.deliveryNoteCustomer?.s3Path} download target='_self'>
                {t('desktop.pages.orders.show.values.transportMedia')}
              </a>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.deliveryNoteSupplier')}>
            {order.deliveryNoteSupplier?.s3Path && (
              <a href={order.deliveryNoteSupplier?.s3Path} download target='_self'>
                {t('desktop.pages.orders.show.values.transportMedia')}
              </a>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.type')}>
            {t(`desktop.pages.orders.show.values.type.${order.type}`)}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.note')}>{order.note}</Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.noteDriver')}>
            {order.noteDriver}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.isPlausible')}>
            {canChangeFinishedOrder ? (
              <Switch onChange={onPlausibleChange} disabled={isPlausibleSubmitting} checked={isPlausible} />
            ) : (
              <>
                {order.isPlausible
                  ? t('desktop.pages.orders.show.values.isPlausible.true')
                  : t('desktop.pages.orders.show.values.isPlausible.false')}
              </>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('desktop.pages.orders.show.labels.weightFormNumber')}>
            {order.weightFormNumber && order.weightFormNumber}
          </Descriptions.Item>
        </Descriptions>
      )}
    </>
  )
}
